// validation.ts

/**
 * Validates if a value is a valid token amount (non-negative integer)
 */
export const isValidTokenAmount = (value: unknown): value is number => {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 0 &&
    Number.isFinite(value)
  );
};

/**
 * Validates if a value is a valid player ID (non-empty string)
 */
export const isValidPlayerId = (value: unknown): value is string => {
  return typeof value === "string" && value.trim().length > 0;
};

/**
 * Validates if a value is a valid array of player IDs with expected count and uniqueness
 */
export const isValidPlayerIds = (
  value: unknown,
  expectedCount: number,
): value is string[] => {
  return (
    Array.isArray(value) &&
    value.length === expectedCount &&
    value.every(isValidPlayerId) &&
    new Set(value).size === value.length // Ensures uniqueness
  );
};

/**
 * Validates if a value is a valid maximum token count (positive integer)
 */
export const isValidMaxTokens = (value: unknown): value is number => {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value > 0 &&
    Number.isFinite(value)
  );
};

/**
 * Validates hook options at initialization
 */
export const validateHookOptions = (options: {
  playerIds: unknown;
  maxTokens: unknown;
  maxTokensPerPlayer: unknown;
  expectedPlayerCount: number;
}): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!isValidPlayerIds(options.playerIds, options.expectedPlayerCount)) {
    errors.push(
      `playerIds must be an array of ${options.expectedPlayerCount} unique, non-empty strings`,
    );
  }

  if (!isValidMaxTokens(options.maxTokens)) {
    errors.push("maxTokens must be a positive integer");
  }

  if (!isValidMaxTokens(options.maxTokensPerPlayer)) {
    errors.push("maxTokensPerPlayer must be a positive integer");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const validators = {
  isValidTokenAmount,
  isValidPlayerId,
  isValidPlayerIds,
  isValidMaxTokens,
  validateHookOptions,
} as const;
